import Card from "components/card";
import { Image } from "components/Image";
import Nft2 from "assets/img/nfts/Nft2.png";
import { Package } from "../types";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "auth/AuthProvider";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { installPackage } from "../api";
import { Loader2 } from "lucide-react";

const PackageCard = (props: {
  extra?: string;
  package: Package;
  onEdit?: (pkg: Package) => void;
}) => {
  const { package: pkg, extra, onEdit } = props;
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const [isInstalling, setIsInstalling] = useState(false);

  // Memoize the image URL to prevent re-creating on every render
  const imageUrl = useMemo(() => {
    if (!pkg.icon_uri) return undefined;
    try {
      const baseUrl = new URL(window.appConfig.apiUrl).origin;

      return `${baseUrl}${pkg.icon_uri}`;
    } catch (error) {
      return undefined;
    }
  }, [pkg.icon_uri, pkg.name]);

  const is_workspace_admin = user.current_workspace.is_workspace_admin;
  const is_super_admin = user.is_super_admin;

  const handleInstall = async () => {
    setIsInstalling(true);
    try {
      await installPackage(pkg.id);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || t("marketplace.installFailed")
      );
    } finally {
      setIsInstalling(false);
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "agent":
        return t("marketplace.typeAgent");
      case "workflow":
        return t("marketplace.typeWorkflow");
      case "integration":
        return t("marketplace.typeIntegration");
      default:
        return type;
    }
  };

  const getPublishStatusLabel = (status: boolean) => {
    if (status) {
      return t("marketplace.statusPublic");
    }
    return t("marketplace.statusUnPublic");
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "agent":
        return "bg-blue-500";
      case "workflow":
        return "bg-green-500";
      case "integration":
        return "bg-purple-500";
      default:
        return "bg-gray-500";
    }
  };

  const getPublishStatusColor = (status: boolean) => {
    if (status) {
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    }

    return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200";
  };

  return (
    <Card
      extra={`flex flex-col w-full h-full !p-5 3xl:p-![18px] bg-white dark:bg-gray-900 ${
        extra || ""
      }`}
    >
      <div className="flex h-full w-full flex-col">
        <div className="flex flex-1 flex-col">
          <div className="mb-4 flex items-start justify-between">
            <div className="flex flex-1 items-start gap-3">
              <Image
                src={imageUrl}
                fallbackSrc={Nft2}
                className="size-[60px] flex-shrink-0 rounded-lg bg-gray-100 object-cover dark:bg-gray-800"
                alt={pkg.name}
              />
              <div className="min-w-0 flex-1">
                <h3 className="mb-1 text-lg font-bold text-navy-700 dark:text-white">
                  {pkg.name}
                </h3>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Type:
                  </span>
                  <span
                    className={`flex w-min items-center justify-center whitespace-nowrap rounded-full px-2 py-1 text-[10px] font-medium text-white ${getTypeColor(
                      pkg.type
                    )}`}
                  >
                    {getTypeLabel(pkg.type)}
                  </span>
                </div>
              </div>
            </div>
            <span
              className={`ml-1 flex items-center justify-center whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium ${getPublishStatusColor(
                pkg.is_publish
              )}`}
            >
              {getPublishStatusLabel(pkg.is_publish)}
            </span>
          </div>

          <p
            className="mb-4 line-clamp-3 text-sm text-gray-700 dark:text-gray-300"
            title={pkg.description}
          >
            {pkg.description}
          </p>

          {pkg?.tags && pkg?.tags?.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-2">
              {pkg.tags.slice(0, 3).map((tag, index) => (
                <span
                  key={index}
                  className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                >
                  {tag}
                </span>
              ))}
              {pkg.tags.length > 3 && (
                <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                  +{pkg.tags.length - 3}
                </span>
              )}
            </div>
          )}

          <div className="mt-auto grid grid-cols-1 gap-2 sm:grid-cols-2 sm:justify-items-stretch">
            <div className="flex justify-start">
              {is_workspace_admin && pkg.is_publish && !is_super_admin ? (
                <button
                  onClick={handleInstall}
                  disabled={isInstalling}
                  className="flex items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-center text-sm font-medium text-white transition duration-200 hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isInstalling ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      {t("marketplace.installing")}
                    </>
                  ) : (
                    t("marketplace.addToWorkspace")
                  )}
                </button>
              ) : (
                <div />
              )}
            </div>
            <div className="hidden">
              {is_super_admin && onEdit ? (
                <button
                  onClick={() => onEdit(pkg)}
                  className="rounded-lg px-4 py-2 text-center text-sm font-medium text-brand-900 transition duration-200 dark:text-brand-400"
                >
                  {t("marketplace.edit")}
                </button>
              ) : (
                <div />
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PackageCard;
